import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/services/guards/auth.guard';
import { LoginComponent } from './layouts/login/login.component';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { DocumentsComponent } from './features/documents/documents.component';
import { HealthComponent } from './features/health/health.component';
import { ConnectionsComponent } from './features/connections/connections.component';
import { CalendarComponent } from './features/calendar/calendar.component';
import { ChatsComponent } from './features/chats/chats.component';
import { NotificationsComponent } from './features/notifications/notifications.component';
import { AudittrailsComponent } from './features/audittrails/audittrails.component';
import { SettingsComponent } from './features/settings/settings.component';
import { LogoutComponent } from './features/logout/logout.component';
import { ResetPasswordComponent } from './layouts/reset-password/reset-password.component';
import { RegisterComponent } from './layouts/register/register.component';
import { ForgotPasswordComponent } from './layouts/forgot-password/forgot-password.component';
import { MailVerificationComponent } from './layouts/mail-verification/mail-verification.component';
import { ProfileInformationComponent } from './features/settings/profile-information/profile-information.component';
import { SubscriptionComponent } from './features/settings/subscription/subscription.component';
import { CountryAffiliationComponent } from './features/settings/country-affiliation/country-affiliation.component';
import { HealthdashbordComponent } from './features/health/healthdashbord/healthdashbord.component';
import { HealthLogComponent } from './features/health/health-log/health-log.component';
import { CarePlanComponent } from './features/health/care-plan/care-plan.component';
import { MedicationComponent } from './features/health/medication/medication.component';
import { AllergyComponent } from './features/health/allergy/allergy.component';
import { HomeComponent } from './features/home/home.component';
import { SearchResultsComponent } from './features/search-results/search-results.component';
import { BookslotComponent } from './features/bookslot/bookslot.component';
import { CheckoutComponent } from './features/bookslot/checkout/checkout.component';
import { ConstructionComponent } from './features/construction/construction.component';
import { FirmDetailsComponent } from './features/firm-details/firm-details.component';
import { AppointmentHistoryComponent } from './features/calendar/appointment-history/appointment-history.component';
import { LegalInformationComponent } from './features/legal-information/legal-information.component';

export const routes: Routes = [
  // Home now lives only at /consumer (the "consumer app" root).
  { path: '', redirectTo: 'consumer', pathMatch: 'full' },
  // Backward compatibility: old bookmarks/links to /home still work.
  { path: 'home', redirectTo: 'consumer', pathMatch: 'full' },

  // --- Backward-compatible redirects for routes moved under /consumer ---
  // Any old hardcoded link/navigate(['/login']) etc. still resolves correctly.
  { path: 'login', redirectTo: 'consumer/login', pathMatch: 'full' },
  { path: 'register', redirectTo: 'consumer/register', pathMatch: 'full' },
  { path: 'mail-verification', redirectTo: 'consumer/mail-verification', pathMatch: 'full' },
  { path: 'forgot-password', redirectTo: 'consumer/forgot-password', pathMatch: 'full' },
  { path: 'reset-password', redirectTo: 'consumer/reset-password', pathMatch: 'full' },
  { path: 'search-results', redirectTo: 'consumer/search-results', pathMatch: 'full' },
  { path: 'bookslot', redirectTo: 'consumer/bookslot', pathMatch: 'full' },
  { path: 'firm/:id', redirectTo: 'consumer/firm/:id', pathMatch: 'full' },
  { path: 'checkout', redirectTo: 'consumer/checkout', pathMatch: 'full' },
  { path: 'videoconsult', redirectTo: 'consumer/videoconsult', pathMatch: 'full' },
  { path: 'legal-verification', redirectTo: 'consumer/legal-verification', pathMatch: 'full' },

  { path: 'dashboard', component: DashboardComponent },
  { path: 'documents', component: DocumentsComponent },
  { path: 'documents/:country', component: DocumentsComponent },
  { path: 'connections', component: ConnectionsComponent },
  { path: 'appointments', component: CalendarComponent },
  { path: 'appointment-history', component: AppointmentHistoryComponent },
  { path: 'chats', component: ChatsComponent },
  { path: 'notifications', component: NotificationsComponent },
  { path: 'audittrails', component: AudittrailsComponent },
  { path: 'settings', component: SettingsComponent },
  { path: 'profile', component: ProfileInformationComponent },
  { path: 'subscription', component: SubscriptionComponent },
  { path: 'country', component: CountryAffiliationComponent },
  // { path: "dashbord", component: HealthdashbordComponent },
  // { path: "health-log", component: HealthLogComponent }, 
  // { path: "care-plan", component: CarePlanComponent },
  // { path: "medication", component: MedicationComponent },
  // { path: "allergy", component: AllergyComponent },
  // { path: "profile/:type", component: AllergyComponent },
  {
        path: 'health',
        loadChildren: () => import('./features/health/health.module')
          .then(m => m.HealthModule)
    },
  //{ path: 'logout', component: LogoutComponent },

  // --- New: all public/consumer-facing routes grouped under /consumer ---
  // This is a "component-less" parent route: it has no `component`, only
  // `children`, so it does NOT introduce a new wrapper component or layout.
  // Its children render into the exact same root outlet as before; only the
  // URL segment changes (e.g. /login -> /consumer/login).
  {
    path: 'consumer',
    children: [
      // Home lives at the bare /consumer path.
      { path: '', component: HomeComponent, pathMatch: 'full' },

      { path: 'login', component: LoginComponent },
      { path: 'register', component: RegisterComponent },
      { path: 'mail-verification', component: MailVerificationComponent },
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'reset-password', component: ResetPasswordComponent },
      { path: 'search-results', component: SearchResultsComponent },
      { path: 'bookslot', component: BookslotComponent },
      { path: 'firm/:id', component: FirmDetailsComponent },
      // { path: 'bookslot/:id', component: BookslotComponent },
      { path: 'checkout', component: CheckoutComponent },
      { path: 'videoconsult', component: ConstructionComponent },
      { path: 'legal-verification', component: ConstructionComponent },

      // Mirrors of the in-app/authenticated routes above (dashboard,
      // documents, connections, appointments, etc.), added here because
      // sidebar/topbar/home/calendar navigation (router.navigate /
      // routerLink) was already updated elsewhere in the app to target
      // '/consumer/appointments', '/consumer/settings', '/consumer/chats',
      // '/consumer/subscription', '/consumer/appointment-history', etc.
      // Without these, those links fell through to the wildcard route.
      // The original unprefixed routes above are left untouched.
      { path: 'dashboard', component: DashboardComponent },
      { path: 'documents', component: DocumentsComponent },
      { path: 'documents/:country', component: DocumentsComponent },
      { path: 'connections', component: ConnectionsComponent },
      { path: 'legal-information', component: LegalInformationComponent },
      { path: 'appointments', component: CalendarComponent },
      { path: 'appointment-history', component: AppointmentHistoryComponent },
      { path: 'chats', component: ChatsComponent },
      { path: 'notifications', component: NotificationsComponent },
      { path: 'audittrails', component: AudittrailsComponent },
      { path: 'settings', component: SettingsComponent },
      { path: 'profile', component: ProfileInformationComponent },
      { path: 'subscription', component: SubscriptionComponent },
      { path: 'country', component: CountryAffiliationComponent },
      {
        path: 'health',
        loadChildren: () => import('./features/health/health.module')
          .then(m => m.HealthModule)
      },

      // Preserved exactly as in the original file. In the original routes
      // array these were always shadowed by the component route of the same
      // path declared earlier (first match wins), so they were effectively
      // unreachable dead code. Kept here, in the same relative order, so
      // behavior is 100% unchanged rather than "fixed" as part of this task.
      {
        path: 'register',
        loadChildren: () => import('./layouts/register/register.module').then(m => m.RegisterModule)
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('./layouts/forgot-password/forgot-password.module').then(m => m.ForgotPasswordModule)
      },
      {
        path: 'reset-password',
        loadChildren: () => import('./layouts/reset-password/reset-password.module').then(m => m.ResetPasswordModule)
      },
      {
        path: 'mail-verification',
        loadChildren: () => import('./layouts/mail-verification/mail-verification.module').then(m => m.MailVerificationModule)
      },
    ]
  },

  // Protected routes (authentication required)
  {
    path: '',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./features/dashboard/dashboard.module')
          .then(m => m.DashboardModule)
      },
      {
        path: 'documents',
        loadChildren: () => import('./features/documents/documents.module')
          .then(m => m.DocumentsModule)
      },
      {
        path: 'connections',
        loadChildren: () => import('./features/connections/connections.module')
          .then(m => m.ConnectionsModule)
      },
      {
        path: 'appointments',
        loadChildren: () => import('./features/calendar/calendar.module')
          .then(m => m.CalendarModule)
      },
      {
        path: 'chats',
        loadChildren: () => import('./features/chats/chats.module')
          .then(m => m.ChatsModule)
      },
      {
        path: 'notifications',
        loadChildren: () => import('./features/notifications/notifications.module')
          .then(m => m.NotificationsModule)
      },
      {
        path: 'audittrails',
        loadChildren: () => import('./features/audittrails/audittrails.module')
          .then(m => m.AudittrailsModule)
      },
      {
        path: 'settings',
        loadChildren: () => import('./features/settings/settings.module')
          .then(m => m.SettingsModule)
      },
      {
        path: 'logout',
        loadChildren: () => import('./features/logout/logout.module')
          .then(m => m.LogoutModule)
      }
    ]
  },

  // Wildcard route for 404
  { path: '**', redirectTo: 'consumer/login' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)], // Removed `useHash: true` for clean URLs
  exports: [RouterModule]
})
export class AppRoutingModule { }